import React, { useEffect, useMemo } from "react";

import { mdiCalendarImport, mdiChartGantt } from "@mdi/js";
import Icon from "@mdi/react";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import moment from "moment";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import FastAPI from "../../../api/FastAPI";
import {
  TIME_FORMAT,
  TIME_FORMAT_WITH_DATE,
} from "../../../constants/dateFormat";
import useFASTScheduleWebSocket from "../../../hooks/useFASTScheduleWebSocket";
import prettyDurations from "../../../utils/durations";
import Input from "../../form/Input";
import Select from "../../form/Select";
import Modal from "../../modal/Modal";

import type { UseMutationResult } from "@tanstack/react-query";
import type { JSX } from "react";

import type { AddScheduleListMediaResponse } from "../../../api/FastAPI.types";
import type {
  Context,
  MutationParams,
} from "../../../hooks/mutations/useAddScheduleListMediaMutation";
import type { MediaLibraryDragItem } from "../../../types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

interface FormValues {
  interstitialAdbreak: {
    value: string;
    label: string;
  } | null;
  startTime: string;
  endTime: string;
}

interface InterstitialAdbreakModalProps {
  addMediaMutation: UseMutationResult<
    AddScheduleListMediaResponse,
    unknown,
    MutationParams,
    Context | null
  >;
  adbreakModalParams: {
    isOpen: boolean;
    media: MediaLibraryDragItem | null;
    startTime: number | null;
    scheduleId: string | null;
    maxEndTime: number | null;
  };
  onClose: () => void;
}

function ModalHeader(): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  return (
    <div className="flex flex-wrap items-center gap-[0.9375rem]">
      <div className="border-border flex rounded-[0.46875rem] border p-[0.46875rem]">
        <Icon path={mdiChartGantt} size={1.25} />
      </div>
      <div className="shrink-0 grow basis-0 text-[1.171875rem] font-semibold">
        {t("interstitialAdbreakModalTitle")}
      </div>
    </div>
  );
}

export default function InterstitialAdbreakModal({
  addMediaMutation,
  adbreakModalParams,
  onClose,
}: InterstitialAdbreakModalProps): JSX.Element {
  const { isOpen, media, startTime, scheduleId, maxEndTime } =
    adbreakModalParams;
  const { t } = useTranslation("editor-fast-channel");
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const mediaDay = moment(startTime);
  const { getCanUpdateSchedule } = useFASTScheduleWebSocket();

  const getAdbreakListQuery = FastAPI.generateQuery({
    fn: FastAPI.getAdBreaksList,
    params: [channelId],
  });
  const { data: getAdbreakListData, isLoading: isLoadingAdbreakList } =
    useQuery(getAdbreakListQuery);

  const adbreakOptions = useMemo(
    () =>
      getAdbreakListData?.data.map((adbreak) => {
        const adbreakPrettyDuration = prettyDurations(adbreak.duration, {
          valueUnit: "ms",
          format: { noDays: true },
        }).humanised;

        return {
          value: adbreak.uuid,
          label: `${adbreak.name} - ${adbreakPrettyDuration}`,
        };
      }),
    [getAdbreakListData],
  );

  const methods = useForm<FormValues>({
    values: {
      interstitialAdbreak: null,
      startTime: moment(startTime).format(TIME_FORMAT_WITH_DATE),
      endTime: moment(startTime)
        .add(media?.duration)
        .format(TIME_FORMAT_WITH_DATE),
    },
  });

  useEffect(() => {
    const selectedAdbreakSubscribtion = methods.watch(
      ({ interstitialAdbreak }, { name }) => {
        if (name === "interstitialAdbreak") {
          const adbreakDuration =
            getAdbreakListData?.data.find(
              (adbreak) => adbreak.uuid === interstitialAdbreak?.value,
            )?.duration ?? 0;
          const cuepointCount = media?.cuepoints?.length ?? 0;
          const addedTime = adbreakDuration * cuepointCount;

          methods.clearErrors("endTime");

          const initialEndTime = moment(startTime).add(media?.duration);

          if (!adbreakDuration) {
            methods.setValue(
              "endTime",
              initialEndTime.format(TIME_FORMAT_WITH_DATE),
            );
          } else {
            const updatedEndTime = initialEndTime.add(addedTime);

            methods.setValue(
              "endTime",
              updatedEndTime.format(TIME_FORMAT_WITH_DATE),
            );

            if (moment(updatedEndTime).isAfter(maxEndTime)) {
              methods.setError("endTime", {
                type: "manual",
              });
            }
          }
        }
      },
    );

    return () => {
      selectedAdbreakSubscribtion.unsubscribe();
    };
  }, [
    methods,
    getAdbreakListData?.data,
    media,
    startTime,
    maxEndTime,
    t,
    mediaDay,
  ]);

  const handleFormSubmit = ({ interstitialAdbreak }: FormValues): void => {
    if (!media || !scheduleId || !startTime) return;

    const adbreakDuration =
      getAdbreakListData?.data.find(
        (adbreak) => adbreak.uuid === interstitialAdbreak?.value,
      )?.duration ?? 0;

    const cuepointsCount = media?.cuepoints?.length ?? 0;
    const newMediaDuration = media.duration + cuepointsCount * adbreakDuration;

    getCanUpdateSchedule({
      onSuccess: () =>
        addMediaMutation?.mutate({
          mediaToAdd: {
            ...media,
            duration: newMediaDuration,
          },
          scheduleId,
          startTime,
          interstitialAdbreakId: interstitialAdbreak?.value,
        }),
      onError: () => {
        toast.error(t("programAddError"));
      },
    });
  };

  const buttonLabel = (
    <div className="flex items-center justify-center gap-[0.46875rem]">
      <Icon path={mdiCalendarImport} size={0.9375} />
      {t("applyToSchedule")}
    </div>
  );

  const hasOverlapError = Boolean(methods.formState.errors.endTime);
  const textClassName = classNames("text-center font-medium", {
    "text-destructive": hasOverlapError,
  });

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <Modal
        isDivided
        header={<ModalHeader />}
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        actions={[
          {
            label: buttonLabel,
            isFullWidth: true,
            onClick: methods.handleSubmit(handleFormSubmit),
            isDisabled: hasOverlapError,
          },
        ]}
      >
        <form className="flex w-full flex-col gap-[0.78125rem]">
          <div className="font-medium">{t("selectInterstitialAdbreak")}</div>
          <Select
            isClearable
            isPortal
            className="mb-[0.625rem]!"
            isLoading={isLoadingAdbreakList}
            label={t("interstitialAdbreak")}
            name="interstitialAdbreak"
            options={adbreakOptions}
            placeholder={t("selectAdbreak")}
            sublabel={`(${t("optionnal", { ns: "forms" })})`}
          />
          <Input
            isReadOnly
            className="mb-[0.625rem]!"
            label={t("startTime")}
            name="startTime"
          />
          <Input
            isReadOnly
            className="mb-[0.625rem]!"
            label={t("projectedEndTime")}
            name="endTime"
          />
          <div className={textClassName}>
            <div>
              {t("nextProgramStartsAt", {
                time: moment(maxEndTime).format(TIME_FORMAT),
              })}
            </div>
            {hasOverlapError ? (
              <div className="text-destructive">
                {t("interstitialAdbreakError.invalidEndTime")}
              </div>
            ) : null}
          </div>
        </form>
      </Modal>
    </FormProvider>
  );
}
